import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../services/api";
import { logout } from "../services/authService";
import { isPunchHiddenForEmployee, isPunchHiddenForCurrentUser } from "../lib/punchAccess";
import "./EmployeeBack.css";

const pad2 = (n) => String(n).padStart(2, "0");
const extractDateListFromFormData = (formData) => {
  if (!formData || typeof formData !== "object") return [];
  let selected = formData.selectedDates;
  if (typeof selected === "string") {
    try { selected = JSON.parse(selected); } catch { selected = []; }
  }
  if (Array.isArray(selected) && selected.length > 0) {
    return selected.map((d) => String(d).slice(0, 10)).filter(Boolean);
  }
  const single = formData.selectedDate || formData.applicationDate || formData.start_date || formData.startDate || "";
  return single ? [String(single).slice(0, 10)] : [];
};

const numberToCircle = (num) => {
  const n = parseInt(num, 10);
  if (Number.isNaN(n)) return num;
  if (n === 0) return "⓪";
  if (n >= 1 && n <= 10) return String.fromCodePoint(9311 + n);
  return String(n);
};

// 権限コード → 管理画面ショートカット（権限を持つ社員の社員証裏に表示する）。
// コードは backend の AVAILABLE_FUNCTIONS（正規コード）と一致させること。
// 遷移先は App.jsx の管理ルート（AdminRoute 配下）。
const ADMIN_SHORTCUTS = [
  { code: "CAN_MANAGE_EVENT",       to: "/admin/create_event",          label: "イベント作成", img: "icon-event.png" },
  { code: "CAN_MANAGE_APPLICATION", to: "/admin/applications",          label: "申請管理",     img: "icon-form.png" },
  { code: "CAN_MANAGE_SALARY",      to: "/admin/salary",                label: "給与管理",     img: "icon-pay.png" },
  { code: "CAN_MANAGE_NOTICE",      to: "/admin/notice",                label: "お知らせ管理", img: "icon-notice.png" },
  { code: "CAN_MANAGE_WORKTIME",    to: "/admin/workstime",             label: "出退勤確認",   img: "icon-time.png" },
  { code: "CAN_MANAGE_EMERGENCY",   to: "/admin/emergency_contact",     label: "緊急連絡網",   img: "icon-emergency_contact.png" },
  { code: "CAN_MANAGE_LEAVE",       to: "/admin/leave",                 label: "有給休暇管理", img: "icon-leave.png" },
  { code: "CAN_MANAGE_QR",          to: "/admin/qr",                    label: "QR生成",       img: "icon-qr.png" },
  { code: "CAN_MANAGE_SETTING",     to: "/admin/employee-card-setting", label: "各マスタ管理", img: "icon-form.png" },
];

// permissions はリスト/カンマ区切り文字列どちらでも受け取れるよう正規化する
const normalizePermissions = (perms) =>
  Array.isArray(perms)
    ? perms.map((p) => String(p).trim()).filter(Boolean)
    : String(perms || "").split(",").map((p) => p.trim()).filter(Boolean);

const EmployeeBack = () => {
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [appNoticeCount, setAppNoticeCount] = useState(0);
  const [eventNoticeCount, setEventNoticeCount] = useState(0);
  const [eventCancelledNotices, setEventCancelledNotices] = useState([]);
  const [showCancelModal, setShowCancelModal] = useState(false);
  //const [remainingLeave, setRemainingLeave] = useState(null);
  const [useEmployeeSchedule, setUseEmployeeSchedule] = useState(false);

  const [backendRemainingLeave, setBackendRemainingLeave] = useState(null);
  const [applicationsList, setApplicationsList] = useState([]);
  const [fiscalStartMonth, setFiscalStartMonth] = useState(4);
  //権限
  const [userPermissions, setUserPermissions] = useState([]);
  // 特定の社員コード（S000/S001 等）では打刻（出退勤）を表示しない
  const [punchHidden, setPunchHidden] = useState(isPunchHiddenForCurrentUser());

  const APPLICATION_NOTICE_TITLES = new Set(["【申請承認のお知らせ】", "【申請却下のお知らせ】", "【申請差し戻しのお知らせ】"]);
  const SYSTEM_AUTO_TITLES = new Set([
    "申請を受け付けました",
    "出勤を記録しました",
    "退勤を記録しました",
    "イベントのお知らせ",
    "イベント中止のお知らせ",
    "スケジュール登録のお願い",
  ]);

  const todayStr = useMemo(() => {
    const today = new Date();
    return `${today.getFullYear()}-${pad2(today.getMonth() + 1)}-${pad2(today.getDate())}`;
  }, []);

  // Xác định năm tài chính hiện tại dựa trên tháng bắt đầu của công ty
  const currentFiscalYear = useMemo(() => {
    const today = new Date();
    const month = today.getMonth() + 1;
    return month >= fiscalStartMonth ? today.getFullYear() : today.getFullYear() - 1;
  }, [fiscalStartMonth]);

  // 残数表示：バックエンドの残日数（付与 − 取得日が到来した消化分）をそのまま使う。
  // ※以前は未到来・申請中の日数を加算していたが、バックエンドが取得日到来分のみ消化に
  //   数える仕様になったため、加算すると過大表示（例: 39→40）になる。加算しない。
  const realRemainingLeave = useMemo(() => {
    if (backendRemainingLeave === null) return null;
    return backendRemainingLeave >= 0 ? backendRemainingLeave : 0;
  }, [backendRemainingLeave]);

  const fetchData = useCallback(async () => {
    try {
      const [empRes, notifRes, compRes] = await Promise.allSettled([
        api.get("/api/employee/me/"),
        api.get("/api/notifications/"),
        api.get("/api/company-info/"),
      ]);

      if (empRes.status === "fulfilled") {
        const emp = empRes.value.data?.employee || empRes.value.data;

        const perms = normalizePermissions(emp?.permissions ?? emp?.user?.permissions);
        setUserPermissions(perms);

        // ここで localStorage の adminUser を書いてはいけない。
        // adminUser は管理者ログイン(authService)が書く共有キーで、localStorage は
        // 同一ホスト名の全タブで共有される。同じブラウザで管理者と社員の両方に
        // ログインしていると、この画面が adminUser を社員の内容で上書きしてしまい、
        // 管理ダッシュボードのアイコンが「権限のあるものだけ表示、他はグレー」になる。
        // （bugfix_同一ブラウザ二重ログイン_20260812.md の BUG-102）
        //
        // 管理ショートカット押下時の通過判定は AdminRoute がサーバ(/api/auth/me/)に
        // 問い合わせて行うため、ここで adminUser を用意する必要はない。

        const memberId = emp?.memberId || emp?.employeeId;
        setPunchHidden(isPunchHiddenForEmployee(emp));
        if (memberId) {
          api.get(`/api/employees/${memberId}/leave/`)
            .then(r => setBackendRemainingLeave(r.data?.remainingLeaveDays ?? 0))
            .catch(() => setBackendRemainingLeave(0));
        }
      }

      if (notifRes.status === "fulfilled") {
        const list = Array.isArray(notifRes.value.data)
          ? notifRes.value.data
          : (notifRes.value.data?.results || []);
        const appNotices = list.filter(n => APPLICATION_NOTICE_TITLES.has(n.title));
        const participationNotices = list.filter(n => n.category === "event");
        const cancelledNotices = list.filter(n => n.category === "event_cancelled");
        const others = list.filter(n =>
          n.category === "company" &&
          !SYSTEM_AUTO_TITLES.has(n.title) &&
          !APPLICATION_NOTICE_TITLES.has(n.title)
        );
        setAppNoticeCount(appNotices.length);
        setEventCancelledNotices(cancelledNotices);
        setEventNoticeCount(participationNotices.length + cancelledNotices.length);
        setUnreadCount(others.length);
      }

      if (compRes.status === "fulfilled") {
        setUseEmployeeSchedule(Boolean(compRes.value.data?.use_employee_schedule));
      }
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const onVis = () => { if (document.visibilityState === "visible") fetchData(); };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [fetchData]);

  const handleLogout = () => {
    if (window.confirm("ログアウトしますか？")) {
      logout();
      navigate("/m/login");
    }
  };

  const handleDoubleTouch = () => navigate("/card_front");

  const handleEventIconClick = (e) => {
    if (eventCancelledNotices.length > 0) {
      e.preventDefault();
      setShowCancelModal(true);
      return;
    }
  };

  const markCancelledNoticesRead = async () => {
    try {
      await Promise.all(
        eventCancelledNotices.map((n) =>
          api.post(`/api/notifications/${n.id}/mark-read/`)
        )
      );
    } catch (err) {
      console.error(err);
    }
    setShowCancelModal(false);
    fetchData();
  };

  const closeCancelModal = () => {
    setShowCancelModal(false);
  };

  return (
    <div onDoubleClick={handleDoubleTouch}>
      <div className="employee-container">
        <div className="employee-back-header">
          <button onClick={handleLogout} className="employee-back-logout">ログアウト</button>
        </div>

        <div className="icon-grid">
          {/* 打刻（特定の社員コードでは非表示） */}
          {!punchHidden && (
            <div className="icon-item">
              <Link to="/worktime" className="icon-box worktime">
                <img src={`${process.env.PUBLIC_URL}/images/icon-time.png`} alt="打刻" />
              </Link>
              <span className="icon-label">打刻</span>
            </div>
          )}

          {/* 申請 */}
          <div className="icon-item">
            <Link to="/applications" className="icon-box applications">
              <img src={`${process.env.PUBLIC_URL}/images/icon-form.png`} alt="申請" />
              {appNoticeCount > 0 && (
                <span className="application-notice-badge">{numberToCircle(appNoticeCount)}</span>
              )}
            </Link>
            <span className="icon-label">申請</span>
          </div>

          {/* イベント */}
          <div className="icon-item">
            <Link
              to="/events"
              className="icon-box event"
              onClick={handleEventIconClick}
            >
              <img src={`${process.env.PUBLIC_URL}/images/icon-event.png`} alt="イベント" />
              {eventNoticeCount > 0 && (
                <span className="event-notice-badge">{numberToCircle(eventNoticeCount)}</span>
              )}
            </Link>
            <span className="icon-label">イベント</span>
          </div>

          {/* お知らせ */}
          <div className="icon-item">
            <Link to="/notice" className="icon-box notice">
              <img src={`${process.env.PUBLIC_URL}/images/icon-notice.png`} alt="お知らせ" />
              {unreadCount > 0 && (
                <span className="notice-badge">{unreadCount > 99 ? "99+" : unreadCount}</span>
              )}
            </Link>
            <span className="icon-label">お知らせ</span>
          </div>

          {/* 給与明細 */}
          <div className="icon-item">
            <Link to="/salary" className="icon-box salary">
              <img src={`${process.env.PUBLIC_URL}/images/icon-pay.png`} alt="給与明細" />
            </Link>
            <span className="icon-label">給与明細</span>
          </div>

          {/* 有給残 → 有給消化日 */}
          <div className="icon-item">
            <Link to="/leave" className="icon-box vacation" aria-label="有給消化日">
              <span className="leave-stack">
                <img
                  className="leave-icon-img"
                  src={`${process.env.PUBLIC_URL}/images/icon-holiday.jpg`}
                  alt=""
                  aria-hidden="true"
                />
                <span className="leave-number" aria-hidden="true">
                  {realRemainingLeave === null ? "..." : numberToCircle(realRemainingLeave)}
                </span>
              </span>
            </Link>
            <span className="icon-label">有給残</span>
          </div>

          {/* 緊急連絡 */}
          <div className="icon-item">
            <Link to="/emergency_contact" className="icon-box emergency">
              <img src={`${process.env.PUBLIC_URL}/images/icon-phone.png`} alt="緊急連絡" />
            </Link>
            <span className="icon-label">緊急連絡</span>
          </div>

          {useEmployeeSchedule && (
            <div className="icon-item">
              <Link to="/schedule" className="icon-box schedule">
                <img src={`${process.env.PUBLIC_URL}/images/icon-calendar.svg`} alt="スケジュール" />
              </Link>
              <span className="icon-label">スケジュール</span>
            </div>
          )}
        </div>

        {(() => {
          const permList = normalizePermissions(userPermissions);
          const shortcuts = ADMIN_SHORTCUTS.filter((f) => permList.includes(f.code));
          if (shortcuts.length === 0) return null;
          return (
            <div className="admin-shortcut-section" style={{ marginTop: '24px', borderTop: '2px dashed #cbd5e1', paddingTop: '16px' }}>
              <h4 style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '10px', fontWeight: 'bold' }}>
                ⚙️ 権限管理画面
              </h4>
              <div className="icon-grid">
                {shortcuts.map((f) => (
                  <div className="icon-item" key={f.code}>
                    <Link to={f.to} className="icon-box admin-shortcut" style={{ backgroundColor: '#fff7ed', border: '2px solid #ea580c' }}>
                      <img src={`${process.env.PUBLIC_URL}/images/${f.img}`} alt={f.label} />
                    </Link>
                    <span className="icon-label" style={{ color: '#ea580c', fontWeight: 'bold' }}>{f.label}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

      </div>

      {showCancelModal && eventCancelledNotices.length > 0 && (
        <div className="eb-modal-overlay" onClick={closeCancelModal} role="presentation">
          <div className="eb-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-labelledby="eb-cancel-title">
            <h3 id="eb-cancel-title" className="eb-modal-title">イベント中止のお知らせ</h3>
            {eventCancelledNotices.map((n) => (
              <div key={n.id} className="eb-cancel-notice-item">
                <p className="eb-cancel-notice-message" style={{ whiteSpace: "pre-wrap" }}>
                  {n.message || n.title}
                </p>
              </div>
            ))}
            <div className="eb-modal-actions">
              <button type="button" className="eb-modal-btn eb-modal-btn-secondary" onClick={closeCancelModal}>
                閉じる
              </button>
              <button type="button" className="eb-modal-btn eb-modal-btn-primary" onClick={() => { markCancelledNoticesRead(); navigate("/events"); }}>
                イベント一覧へ
              </button>
              <button type="button" className="eb-modal-btn eb-modal-btn-primary" onClick={markCancelledNoticesRead}>
                確認しました
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeBack;
